import { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import { VIDEO_NAME, YOUTUBE_EMBED_URL } from "../content/demoVideo";
import { useFocusTrap } from "../lib/useFocusTrap";

type ExpandedVideoModalProps = {
  open: boolean;
  onClose: () => void;
  startSecond?: number;
};

export default function ExpandedVideoModal({
  open,
  onClose,
  startSecond = 0,
}: ExpandedVideoModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const iframeSrc = `${YOUTUBE_EMBED_URL}?autoplay=1&rel=0${startSecond > 0 ? `&start=${startSecond}` : ""}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-2 sm:p-4 md:p-8"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-full w-full max-w-7xl max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-2xl border border-border bg-bg-secondary p-2 shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:p-3 md:max-h-[calc(100dvh-4rem)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between sm:mb-3">
          <div
            id={titleId}
            className="text-xs font-semibold uppercase tracking-[0.16em] text-text-faint"
          >
            Expanded walkthrough
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-bg px-2.5 py-1.5 text-xs font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover sm:px-3"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Close
          </button>
        </div>
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-black">
          <iframe
            src={iframeSrc}
            title={VIDEO_NAME}
            className="h-full w-full border-0"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}
